<template>
  <v-dialog :value="value" @input="$emit('input', $event)" persistent max-width="700" scrollable>
    <v-card>
      <v-toolbar color="success darken-2" dark flat>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">{{ $t('memberDataSection.greenCoffee.cupping') }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-6" style="max-height: calc(90vh - 140px); overflow-y: auto;">
        <h2 class="text-h6 font-weight-bold black--text mb-4">{{ $t('dashboardSection.greenCoffee') }}</h2>

        <!-- Cupper Name & Date -->
        <v-row>
          <v-col cols="12" sm="6">
            <div class="mb-1 text-body-2">{{ $t('memberDataSection.greenCoffee.cupperName') }}</div>
            <v-text-field
              v-model="form.cupperName"
              :placeholder="$t('memberDataSection.greenCoffee.enterCupperName')"
              outlined
              dense
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="mb-1 text-body-2">{{ $t('memberDataSection.greenCoffee.cuppingDate') }}</div>
            <v-menu v-model="dateMenu" :close-on-content-click="true" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedCuppingDate"
                  readonly
                  outlined
                  dense
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  append-icon="mdi-calendar"
                ></v-text-field>
              </template>
              <v-date-picker v-model="form.cuppingDate" no-title @input="dateMenu = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <!-- Attach File(s) -->
        <div class="mt-6">
          <div class="d-flex align-center mb-2">
            <span class="text-body-2">{{ $t('memberDataSection.greenCoffee.attachFiles') }}</span>
            <v-icon small class="ml-1" color="grey">mdi-information-outline</v-icon>
          </div>
          <div
            class="upload-zone pa-6 text-center"
            @dragover.prevent="dragOver = true"
            @dragleave="dragOver = false"
            @drop.prevent="onFilesDrop"
            :class="{ 'upload-zone--active': dragOver }"
          >
            <v-icon size="40" color="grey lighten-1">mdi-upload</v-icon>
            <p class="mt-2 mb-0 text-body-2">
              <a href="javascript:void(0)" class="primary--text" @click="$refs.fileInput.click()">{{ $t('memberDataSection.greenCoffee.selectFilesToUpload') }}</a>
              {{ $t('memberDataSection.greenCoffee.orDragAndDrop') }}
            </p>
            <input ref="fileInput" type="file" multiple class="d-none" @change="onFileSelect" />
          </div>
          <div v-if="form.files.length" class="d-flex flex-wrap mt-2">
            <div v-for="(f, i) in form.files" :key="i" class="file-preview ma-1">
              <v-icon small @click="removeFile(i)" class="file-remove">mdi-close</v-icon>
              <v-icon class="file-icon">mdi-download</v-icon>
              <span class="file-name">{{ f.name || 'File' }}</span>
            </div>
          </div>
        </div>

        <!-- Roasting: Light Roast -->
        <div class="mt-6 roasting-card rounded pa-4">
          <div class="d-flex justify-end mb-3">
            <span class="text-subtitle-1 black--text">Light Roast</span>
          </div>
          <div class="mb-4">
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="d-flex align-center">
                <span class="text-body-2 font-weight-medium">Roasting Time</span>
                <v-icon small class="ml-1" color="grey">mdi-information-outline</v-icon>
              </div>
              <span class="text-h6 black--text">Light Roast ({{ form.roastingTime }}m)</span>
            </div>
            <v-slider v-model="form.roastingTime" min="8" max="20" step="0.5" thumb-label="always" ticks="always" tick-size="3" color="brown darken-3" track-color="brown darken-3" hide-details class="cupping-slider mt-2"></v-slider>
            <div class="d-flex justify-space-between text-caption text--secondary px-1 mt-1">
              <span>8 min</span><span>20 min</span>
            </div>
          </div>
          <div>
            <div class="d-flex align-center justify-space-between mb-1">
              <div class="d-flex align-center">
                <span class="text-body-2 font-weight-medium">Roasting Temperature</span>
                <v-icon small class="ml-1" color="grey">mdi-information-outline</v-icon>
              </div>
              <div class="d-flex align-center">
                <template v-if="editingTempC">
                  <v-text-field
                    v-model.number="form.roastingTempC"
                    type="number"
                    dense
                    outlined
                    hide-details
                    autofocus
                    class="temp-c-input mr-1"
                    suffix="°C"
                    @blur="commitTempC"
                    @keydown.enter="commitTempC"
                    @keydown.esc="editingTempC = false"
                  ></v-text-field>
                </template>
                <template v-else>
                  <span class="text-h6 black--text">{{ form.roastingTempC }}°C</span>
                  <v-icon small class="ml-1" color="brown darken-3" style="cursor:pointer" @click="editingTempC = true">mdi-pencil</v-icon>
                </template>
              </div>
            </div>
            <v-slider v-model="form.roastingTemperature" min="180" max="240" step="5" thumb-label="always" ticks="always" tick-size="3" color="brown darken-3" track-color="brown darken-3" hide-details class="cupping-slider mt-2"></v-slider>
            <div class="d-flex justify-space-between text-caption text--secondary px-1 mt-1">
              <span>180°C</span><span>240°C</span>
            </div>
          </div>
        </div>

        <!-- Sensory: Fragrance/Aromas, Flavor, After Taste, Acidity, Body -->
        <div v-for="attr in sensoryAttributes" :key="attr.key" class="mt-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-bold black--text">{{ attr.label }}</span>
              <v-icon small class="ml-1" color="success">mdi-information-outline</v-icon>
            </div>
            <span class="text-h6 success--text">{{ (form[attr.key] && form[attr.key].score) || 0 }}</span>
          </div>
          <v-slider
            :value="form[attr.key] && form[attr.key].score"
            @input="setAttrScore(attr.key, $event)"
            min="6"
            max="10"
            step="0.25"
            thumb-label="always"
            ticks="always"
            tick-size="3"
            color="success"
            track-color="success"
            hide-details
            class="green-slider mt-4 mb-1"
          ></v-slider>
          <div class="d-flex justify-space-between text-caption text--secondary px-1 mb-3">
            <span>6</span><span>10</span>
          </div>
          <template v-if="attr.hasSubSliders && (form[attr.key] && form[attr.key].sub)">
            <v-row dense class="mt-2">
              <v-col
                v-for="(subLabel, si) in attr.subSliderLabels"
                :key="'sub'+si"
                cols="12"
                :sm="attr.subSliderLabels.length > 1 ? 6 : 12"
              >
                <div class="sub-slider-wrap">
                  <v-slider
                    :value="form[attr.key].sub[si]"
                    @input="setAttrSub(attr.key, si, $event)"
                    min="0"
                    max="1"
                    step="0.01"
                    thumb-label="always"
                    ticks="always"
                    tick-size="3"
                    color="success"
                    track-color="success"
                    hide-details
                    class="green-slider sub-slider mt-4"
                  >
                    <template v-slot:thumb-label="{ value }">
                      {{ subValueLabel(value) }}
                    </template>
                  </v-slider>
                  <div class="d-flex justify-space-between text-caption text--secondary px-1 mt-1">
                    <span>Low</span><span>High</span>
                  </div>
                  <div class="text-caption text-center font-weight-bold mt-1">{{ subLabel }}</div>
                </div>
              </v-col>
            </v-row>
          </template>
          <div class="mt-2">
            <div class="d-flex align-center mb-1">
              <span class="text-body-2">Qualities</span>
              <v-icon small class="ml-1" color="success">mdi-information-outline</v-icon>
            </div>
            <div class="d-flex align-center">
              <v-text-field
                :value="qualityInput[attr.key]"
                @input="qualityInput[attr.key] = $event"
                placeholder="| Type"
                outlined
                dense
                hide-details
                class="mr-2"
                style="max-width: 200px;"
                @keydown.enter.prevent="addQuality(attr.key)"
              ></v-text-field>
              <v-btn fab small color="success" class="white--text" @click="addQuality(attr.key)">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
            <div v-if="form[attr.key] && form[attr.key].qualities && form[attr.key].qualities.length" class="d-flex flex-wrap mt-2">
              <v-chip
                v-for="(q, qi) in form[attr.key].qualities"
                :key="qi"
                small
                close
                close-icon="mdi-close"
                class="mr-1 mb-1"
                @click:close="removeQuality(attr.key, qi)"
              >{{ q }}</v-chip>
            </div>
          </div>
        </div>

        <!-- Uniformity: 5 checkboxes -->
        <div class="mt-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-bold black--text">Uniformity</span>
              <v-icon small class="ml-1" color="success">mdi-information-outline</v-icon>
            </div>
            <span class="text-h6 success--text">{{ form.uniformity }}</span>
          </div>
          <div class="d-flex">
            <v-checkbox v-for="n in 5" :key="'u'+n" v-model="form.uniformityChecks" :value="n" hide-details class="mt-0 mr-2" color="success"></v-checkbox>
          </div>
        </div>

        <!-- Clean Cup -->
        <div class="mt-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-bold black--text">Clean Cup</span>
              <v-icon small class="ml-1" color="success">mdi-information-outline</v-icon>
            </div>
            <span class="text-h6 success--text">{{ form.cleanCup }}</span>
          </div>
          <div class="d-flex">
            <v-checkbox v-for="n in 5" :key="'c'+n" v-model="form.cleanCupChecks" :value="n" hide-details class="mt-0 mr-2" color="success"></v-checkbox>
          </div>
        </div>

        <!-- Sweetness -->
        <div class="mt-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-bold black--text">Sweetness</span>
              <v-icon small class="ml-1" color="success">mdi-information-outline</v-icon>
            </div>
            <span class="text-h6 success--text">{{ form.sweetness }}</span>
          </div>
          <div class="d-flex">
            <v-checkbox v-for="n in 5" :key="'s'+n" v-model="form.sweetnessChecks" :value="n" hide-details class="mt-0 mr-2" color="success"></v-checkbox>
          </div>
        </div>

        <!-- Overall -->
        <div class="mt-6">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <span class="text-subtitle-1 font-weight-bold black--text">Overall</span>
              <v-icon small class="ml-1" color="success">mdi-information-outline</v-icon>
            </div>
            <span class="text-h6 success--text">{{ form.overall }}</span>
          </div>
          <div class="d-flex align-center">
            <span class="slider-value-box mr-2">{{ form.overall }}</span>
            <v-slider v-model="form.overall" min="6" max="10" step="0.25" thumb-label="always" hide-details class="cupping-slider green-slider"></v-slider>
          </div>
        </div>

        <!-- Defects -->
        <div class="mt-6">
          <div class="d-flex align-center mb-2">
            <span class="text-subtitle-1 font-weight-bold black--text">Defects</span>
            <v-icon small class="ml-1" color="success">mdi-information-outline</v-icon>
          </div>
          <p class="text-body-2 mb-2">Taint=2 Fault=4</p>
          <div class="d-flex align-center flex-wrap">
            <span class="mr-2"># of cups</span>
            <v-text-field v-model.number="form.defectCups" type="number" min="0" outlined dense hide-details class="defect-input"></v-text-field>
            <span class="mx-2">×</span>
            <span class="mr-2">Intensity</span>
            <v-text-field v-model.number="form.defectIntensity" type="number" min="0" outlined dense hide-details class="defect-input"></v-text-field>
            <span class="mx-2">=</span>
            <span class="font-weight-bold">{{ defectPoints.toFixed(1) }}</span>
          </div>
        </div>

        <!-- Final Score -->
        <div class="mt-6 d-flex align-center justify-space-between py-3">
          <span class="text-h6 font-weight-bold">Final Score</span>
          <span class="text-h4 font-weight-bold orange--text">{{ finalScore.toFixed(2) }}</span>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn text color="orange darken-2" class="text-uppercase" @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn color="orange darken-2" class="white--text text-uppercase" depressed @click="onSubmit">{{ $t('submit') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';

const defaultScore = 8;
const sensoryAttributes = [
  { key: 'fragranceAromas', label: 'Fragrance/Aromas', hasSubSliders: true, subSliderLabels: ['Break', 'Dry'] },
  { key: 'flavor', label: 'Flavor', hasSubSliders: false },
  { key: 'afterTaste', label: 'After Taste', hasSubSliders: false },
  { key: 'acidity', label: 'Acidity', hasSubSliders: true, subSliderLabels: ['Intensity'] },
  { key: 'body', label: 'Body', hasSubSliders: true, subSliderLabels: ['Level'] },
];

export default {
  name: 'StartCuppingModal',
  props: {
    value: { type: Boolean, default: false },
    greenCoffeeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      dateMenu: false,
      dragOver: false,
      editingTempC: false,
      qualityInput: {},
      sensoryAttributes,
      form: {
        cupperName: '',
        cuppingDate: new Date().toISOString().split('T')[0],
        files: [],
        roastingTime: 8,
        roastingTemperature: 180,
        roastingTempC: 180,
        fragranceAromas: { score: defaultScore, sub: [0.5, 0.5], qualities: [] },
        flavor: { score: defaultScore, qualities: [] },
        afterTaste: { score: defaultScore, qualities: [] },
        acidity: { score: defaultScore, sub: [0.5], qualities: [] },
        body: { score: defaultScore, sub: [0.5], qualities: [] },
        uniformityChecks: [],
        uniformity: 0,
        cleanCupChecks: [],
        cleanCup: 0,
        sweetnessChecks: [],
        sweetness: 0,
        overall: defaultScore,
        defectCups: 1,
        defectIntensity: 2,
      },
    };
  },
  computed: {
    formattedCuppingDate() {
      return this.form.cuppingDate ? moment(this.form.cuppingDate).format('DD-MMM-YYYY').toUpperCase() : '';
    },
    defectPoints() {
      const c = Number(this.form.defectCups) || 0;
      const i = Number(this.form.defectIntensity) || 0;
      return c * i;
    },
    finalScore() {
      const scores = [
        this.form.fragranceAromas?.score,
        this.form.flavor?.score,
        this.form.afterTaste?.score,
        this.form.acidity?.score,
        this.form.body?.score,
        this.form.overall,
      ].filter((s) => s != null);
      const checkScores = [this.form.uniformity, this.form.cleanCup, this.form.sweetness].filter((s) => s != null);
      const total = scores.reduce((a, b) => a + (Number(b) || 0), 0) + checkScores.reduce((a, b) => a + (Number(b) || 0), 0);
      return Math.max(0, total - this.defectPoints);
    },
  },
  watch: {
    'form.uniformityChecks'(v) {
      const count = Array.isArray(v) ? v.length : 0;
      this.form.uniformity = count * 2;
    },
    'form.cleanCupChecks'(v) {
      const count = Array.isArray(v) ? v.length : 0;
      this.form.cleanCup = count * 2;
    },
    'form.sweetnessChecks'(v) {
      const count = Array.isArray(v) ? v.length : 0;
      this.form.sweetness = count * 2;
    },
    // Slider → °C field
    'form.roastingTemperature'(v) {
      this.form.roastingTempC = v;
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
      this.$emit('close');
    },
    commitTempC() {
      const val = Number(this.form.roastingTempC);
      if (!isNaN(val)) {
        const clamped = Math.min(240, Math.max(180, val));
        const snapped = Math.round(clamped / 5) * 5;
        this.form.roastingTemperature = snapped;
        this.form.roastingTempC = snapped;
      }
      this.editingTempC = false;
    },
    onFilesDrop(e) {
      this.dragOver = false;
      const files = e.dataTransfer?.files;
      if (files?.length) this.addFiles(Array.from(files));
    },
    onFileSelect(e) {
      const files = e.target?.files;
      if (files?.length) this.addFiles(Array.from(files));
      e.target.value = '';
    },
    addFiles(files) {
      this.form.files = this.form.files.concat(files.map((f) => ({ file: f, name: f.name })));
    },
    removeFile(i) {
      this.form.files.splice(i, 1);
    },
    setAttrScore(key, v) {
      if (!this.form[key]) this.form[key] = { score: defaultScore, sub: [], qualities: [] };
      this.$set(this.form[key], 'score', v);
    },
    setAttrSub(key, index, v) {
      if (!this.form[key]) return;
      if (!this.form[key].sub) this.$set(this.form[key], 'sub', []);
      this.$set(this.form[key].sub, index, v);
    },
    subValueLabel(v) {
      if (v == null || v === undefined) return 'Low';
      return v < 0.5 ? 'Low' : 'High';
    },
    addQuality(key) {
      const input = this.qualityInput[key];
      if (!input || !input.trim()) return;
      if (!this.form[key]) this.form[key] = { score: defaultScore, sub: [], qualities: [] };
      if (!this.form[key].qualities) this.$set(this.form[key], 'qualities', []);
      this.form[key].qualities.push(input.trim());
      this.$set(this.qualityInput, key, '');
    },
    removeQuality(key, index) {
      if (this.form[key] && this.form[key].qualities) this.form[key].qualities.splice(index, 1);
    },
    onSubmit() {
      const payload = {
        greenCoffeeId: this.greenCoffeeId,
        cupperName: this.form.cupperName,
        cuppingDate: this.form.cuppingDate,
        roastingTime: this.form.roastingTime,
        roastingTemperature: this.form.roastingTemperature,
        roastingTempC: this.form.roastingTempC,
        fragranceAromas: this.form.fragranceAromas,
        flavor: this.form.flavor,
        afterTaste: this.form.afterTaste,
        acidity: this.form.acidity,
        body: this.form.body,
        uniformity: this.form.uniformity,
        cleanCup: this.form.cleanCup,
        sweetness: this.form.sweetness,
        overall: this.form.overall,
        defectCups: this.form.defectCups,
        defectIntensity: this.form.defectIntensity,
        defectPoints: this.defectPoints,
        finalScore: this.finalScore,
        files: this.form.files,
      };
      this.$emit('submit', payload);
      this.close();
    },
  },
};
</script>

<style scoped>
.upload-zone {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}
.upload-zone--active {
  border-color: var(--v-primary-base);
  background: #f0f7f0;
}
.file-preview {
  width: 100px;
  height: 100px;
  background: #e8f5e9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}
.file-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  cursor: pointer;
}
.file-name {
  font-size: 10px;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90px;
}
.roasting-card {
  background-color: #f2f2f2;
}
.slider-value-box {
  min-width: 44px;
  padding: 2px 8px;
  background: #eeeeee;
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
  flex-shrink: 0;
}
/* Roasting sliders */
.cupping-slider >>> .v-slider__thumb {
  background: white !important;
  border: 3px solid #4e342e !important;
  width: 22px !important;
  height: 22px !important;
}
.cupping-slider >>> .v-slider__track-fill,
.cupping-slider >>> .v-slider__track-background {
  background: #6d4c41 !important;
}
.cupping-slider >>> .v-slider__tick {
  background: rgba(255,255,255,0.6) !important;
}
/* Gray flat rounded-rect thumb label */
.cupping-slider >>> .v-slider__thumb-label {
  background: #616161 !important;
  border-radius: 4px !important;
  width: 40px !important;
  height: 26px !important;
  transform: translateY(-20%) translateY(-14px) translateX(-50%) !important;
}
.cupping-slider >>> .v-slider__thumb-label > span {
  transform: none !important;
}
/* Sensory/green sliders */
.green-slider >>> .v-slider__track-fill,
.green-slider >>> .v-slider__track-background {
  background: #4caf50 !important;
}
.green-slider >>> .v-slider__tick {
  background: rgba(255,255,255,0.6) !important;
}
.green-slider >>> .v-slider__thumb {
  background: white !important;
  border: 3px solid #4caf50 !important;
  width: 18px !important;
  height: 18px !important;
}
/* Gray flat rounded-rect thumb label for all green sliders */
.green-slider >>> .v-slider__thumb-label {
  background: #616161 !important;
  border-radius: 4px !important;
  width: 40px !important;
  height: 26px !important;
  transform: translateY(-20%) translateY(-14px) translateX(-50%) !important;
}
.green-slider >>> .v-slider__thumb-label > span {
  transform: none !important;
}
.sub-slider-wrap {
  padding: 0 4px;
}
.defect-input {
  max-width: 80px;
}
.temp-c-input {
  max-width: 100px;
}

::v-deep .v-slider--horizontal .v-slider__track-container{
  height: 4px !important;
}

::v-deep .v-slider--horizontal .v-slider__thumb-label > *{
  transform: none !important;
}
</style>
